import React, { useRef, useState, useEffect, useCallback } from 'react';
import apiClient from '@lib/api/client';
import { Modal } from '@components/common';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { InvoiceItemsTable } from './InvoiceItemsTable';
import { PrintableInvoice } from './PrintableInvoice';
import { InvoiceAttachmentsSection } from './InvoiceAttachmentsSection';
import { JobDocumentPickerModal } from './JobDocumentPickerModal';
import { printInvoice } from '@lib/utils/generateInvoicePdf';
import { generateInvoiceWithAttachments } from '@lib/utils/generateInvoiceWithAttachments';
import { getSettings } from '@lib/api/settings';
import { getInvoice as fetchInvoiceDetail, releaseHoldback } from '@lib/api/invoices';
import type { InvoiceDetail, InvoicePayment, InvoiceRevision } from '@lib/api/invoices';
import { useToast } from '@lib/hooks/useToast';
import { ToastContainer } from '@components/common/Toast';
import { ConfirmDialog } from '@components/common';

interface InvoiceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: InvoiceDetail | null;
  onEdit?: () => void;
  onSend?: () => void;
  onRecordPayment?: () => void;
  onDelete?: () => void;
  onViewRevision?: (invoiceId: number) => void;
  onRefresh?: (updated: InvoiceDetail) => void;
}

export const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  isOpen,
  onClose,
  invoice,
  onEdit,
  onSend,
  onRecordPayment,
  onDelete,
  onViewRevision,
  onRefresh,
}) => {
  const printableRef = useRef<HTMLDivElement>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const { toast, toasts, removeToast } = useToast();
  const [showPreview, setShowPreview] = useState(false);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [showHoldbackConfirm, setShowHoldbackConfirm] = useState(false);
  const [releasingHoldback, setReleasingHoldback] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<{
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
    gst?: string;
  } | undefined>(undefined);
  const [invoiceFooter, setInvoiceFooter] = useState<string | undefined>(undefined);

  // Fetch company settings when modal opens
  useEffect(() => {
    if (isOpen) {
      getSettings()
        .then((settings) => {
          if (settings.company) {
            const c = settings.company;
            // Build full address from components
            const addressParts = [
              c.address,
              c.city && c.state ? `${c.city}, ${c.state}` : c.city || c.state,
              c.zipCode,
            ].filter(Boolean);

            setCompanyInfo({
              name: c.companyName || 'Your Company',
              address: addressParts.join('\n'),
              phone: c.phone || '',
              email: c.email || '',
              logo: c.logoUrl,
              gst: c.gstNumber || undefined,
            });
            setInvoiceFooter(c.invoiceFooter);
          }
        })
        .catch((err) => {
          console.error('Failed to load company settings:', err);
        });
    }
  }, [isOpen]);

  const handleAttachmentsChange = useCallback(async () => {
    if (!invoice) return;
    try {
      const updated = await fetchInvoiceDetail(invoice.id);
      onRefresh?.(updated);
    } catch (err) {
      console.error('Failed to refresh invoice:', err);
    }
  }, [invoice, onRefresh]);

  const handleReleaseHoldback = async () => {
    if (!invoice) return;
    setReleasingHoldback(true);
    try {
      await releaseHoldback(invoice.id);
      toast.success('Holdback released successfully.');
      const updated = await fetchInvoiceDetail(invoice.id);
      onRefresh?.(updated);
    } catch (err) {
      console.error('Failed to release holdback:', err);
      toast.error('Failed to release holdback. Please try again.');
    } finally {
      setReleasingHoldback(false);
      setShowHoldbackConfirm(false);
    }
  };

  if (!invoice) return null;

  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;
    setGeneratingPdf(true);
    try {
      await generateInvoiceWithAttachments(
        printableRef.current,
        `${invoice.invoice_number}.pdf`,
        invoice.attachments || []
      );
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handlePrint = () => {
    if (!printableRef.current) return;
    printInvoice(printableRef.current);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const canEdit = !['paid', 'cancelled', 'revised'].includes(invoice.status);
  const canSend = ['draft', 'overdue'].includes(invoice.status);
  const canRecordPayment = !['paid', 'cancelled', 'revised'].includes(invoice.status);
  const canDelete = !['paid'].includes(invoice.status);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Invoice ${invoice.invoice_number}`}
      size="lg"
      footer={
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            {canDelete && onDelete && (
              <button
                onClick={onDelete}
                className="px-4 py-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
              >
                Delete
              </button>
            )}
            {invoice.holdback_amount != null &&
              invoice.holdback_amount > 0 &&
              !invoice.holdback_released_at && (
                <button
                  onClick={() => setShowHoldbackConfirm(true)}
                  disabled={releasingHoldback}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-60"
                >
                  <span className="material-symbols-outlined text-base">lock_open</span>
                  {releasingHoldback ? 'Releasing…' : 'Release Holdback'}
                </button>
              )}
          </div>
          <div className="flex gap-2">
            {/* Preview/Print/PDF Buttons */}
            <button
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
              title="Preview Invoice"
            >
              <span className="material-symbols-outlined text-lg">visibility</span>
              Preview
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
              title="Print Invoice"
            >
              <span className="material-symbols-outlined text-lg">print</span>
              Print
            </button>
            <button
              onClick={handleDownloadPdf}
              disabled={generatingPdf}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors disabled:opacity-50"
              title="Download PDF"
            >
              <span className="material-symbols-outlined text-lg">download</span>
              {generatingPdf ? 'Generating...' : 'PDF'}
            </button>
            {canEdit && onEdit && (
              <button
                onClick={onEdit}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors"
              >
                Edit
              </button>
            )}
            {canSend && onSend && (
              <button
                onClick={onSend}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Send Invoice
              </button>
            )}
            {canRecordPayment && onRecordPayment && (
              <button
                onClick={onRecordPayment}
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                Record Payment
              </button>
            )}
            {canRecordPayment && invoice && invoice.balance_due > 0 && (
              <PayNowButton invoiceId={invoice.id} />
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header Info */}
        <div className="flex justify-between items-start">
          <div>
            <div className="text-2xl font-bold text-white mb-2">
              {invoice.invoice_number}
            </div>
            <InvoiceStatusBadge status={invoice.status} size="lg" />
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-400">Total Amount</div>
            <div className="text-2xl font-bold text-white">{formatCurrency(invoice.total)}</div>
            {invoice.balance_due > 0 && invoice.balance_due < invoice.total && (
              <div className="text-sm text-orange-400">
                Balance Due: {formatCurrency(invoice.balance_due)}
              </div>
            )}
          </div>
        </div>

        {/* Revised Banner */}
        {invoice.status === 'revised' && (
          <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 flex items-center gap-2">
            <span className="material-symbols-outlined text-purple-400">info</span>
            <span className="text-purple-300 text-sm">
              This invoice has been superseded by a newer revision.
            </span>
          </div>
        )}

        {/* Client & Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#101922] p-4 rounded-lg border border-gray-700">
            <div className="text-sm text-gray-400 mb-1">Bill To</div>
            <div className="text-white font-medium">{invoice.client_name}</div>
            {invoice.client_company && (
              <div className="text-gray-400 text-sm">{invoice.client_company}</div>
            )}
            {invoice.client_email && (
              <div className="text-gray-400 text-sm">{invoice.client_email}</div>
            )}
            {invoice.client_phone && (
              <div className="text-gray-400 text-sm">{invoice.client_phone}</div>
            )}
            {invoice.client_address && (
              <div className="text-gray-400 text-sm mt-2">{invoice.client_address}</div>
            )}
          </div>

          <div className="bg-[#101922] p-4 rounded-lg border border-gray-700 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-400">Invoice Date:</span>
              <span className="text-white">{formatDate(invoice.invoice_date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Due Date:</span>
              <span className={`${invoice.status === 'overdue' ? 'text-red-400' : 'text-white'}`}>
                {formatDate(invoice.due_date)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Payment Terms:</span>
              <span className="text-white">{invoice.payment_terms} days</span>
            </div>
            {invoice.job_title && (
              <div className="flex justify-between">
                <span className="text-gray-400">Job:</span>
                <span className="text-white">{invoice.job_title}</span>
              </div>
            )}
            {invoice.quote_number && (
              <div className="flex justify-between">
                <span className="text-gray-400">Quote:</span>
                <span className="text-white">{invoice.quote_number}</span>
              </div>
            )}
          </div>
        </div>

        {/* Line Items */}
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">Line Items</h3>
          <InvoiceItemsTable
            items={invoice.items.map((item) => ({
              description: item.description,
              quantity: item.quantity,
              unit: item.unit,
              unit_price: item.unit_price,
            }))}
            readOnly
          />
        </div>

        {/* Totals */}
        <div className="bg-[#101922] p-4 rounded-lg border border-gray-700">
          <div className="space-y-2">
            <div className="flex justify-between text-gray-400">
              <span>Subtotal</span>
              <span>{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.gst_amount !== undefined && invoice.gst_amount > 0 ? (
              <>
                <div className="flex justify-between text-gray-400">
                  <span>GST ({Number(invoice.gst_rate || 0) * 100}%)</span>
                  <span>{formatCurrency(invoice.gst_amount)}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>PST ({Number(invoice.pst_rate || 0) * 100}%)</span>
                  <span>{formatCurrency(invoice.pst_amount || 0)}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between text-gray-400">
                <span>Tax ({invoice.tax_rate}%)</span>
                <span>{formatCurrency(invoice.tax_amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-white font-semibold text-lg border-t border-gray-700 pt-2">
              <span>Total</span>
              <span>{formatCurrency(invoice.total)}</span>
            </div>
            
            {invoice.holdback_amount !== undefined && invoice.holdback_amount > 0 && (
              <div className="border-t border-dashed border-gray-700 pt-2 mt-1">
                <div className={`flex justify-between ${invoice.holdback_released_at ? 'text-green-400' : 'text-orange-400'}`}>
                  <span className="text-sm">
                    Holdback ({Number(invoice.holdback_percent || 0) * 100}%)
                    {invoice.holdback_released_at && (
                      <span className="ml-1 text-xs opacity-80">— Released</span>
                    )}
                  </span>
                  <span>{invoice.holdback_released_at ? '' : '-'}{formatCurrency(invoice.holdback_amount)}</span>
                </div>
                {invoice.holdback_released_at && (
                  <div className="text-xs text-green-500 mt-0.5">
                    Released on {formatDate(invoice.holdback_released_at)}
                  </div>
                )}
              </div>
            )}

            {invoice.amount_paid > 0 && (
              <div className="flex justify-between text-green-400">
                <span>Amount Paid</span>
                <span>-{formatCurrency(invoice.amount_paid)}</span>
              </div>
            )}
            
            <div className="flex justify-between text-orange-400 font-bold text-xl border-t border-gray-700 pt-2 mt-2">
              <span>Balance Due</span>
              <span>{formatCurrency(invoice.balance_due)}</span>
            </div>
          </div>
        </div>

        {/* Payment History */}
        {invoice.payments && invoice.payments.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Payment History</h3>
            <div className="space-y-2">
              {invoice.payments.map((payment: InvoicePayment) => (
                <div
                  key={payment.id}
                  className="flex justify-between items-center bg-[#101922] p-3 rounded-lg border border-gray-700"
                >
                  <div>
                    <div className="text-white font-medium">
                      {formatCurrency(payment.amount)}
                    </div>
                    <div className="text-sm text-gray-400">
                      {formatDate(payment.payment_date)}
                      {payment.payment_method && ` - ${payment.payment_method}`}
                      {payment.reference_number && ` (${payment.reference_number})`}
                    </div>
                    {payment.notes && (
                      <div className="text-sm text-gray-500 mt-1">{payment.notes}</div>
                    )}
                  </div>
                  <span className="material-symbols-outlined text-green-400">check_circle</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Notes</h3>
            <div className="text-gray-400 bg-[#101922] p-4 rounded-lg border border-gray-700">
              {invoice.notes}
            </div>
          </div>
        )}

        {/* Attachments */}
        <InvoiceAttachmentsSection
          invoiceId={invoice.id}
          jobId={invoice.job_id}
          attachments={invoice.attachments || []}
          onAttachmentsChange={handleAttachmentsChange}
          onPickFromDocuments={invoice.job_id ? () => setShowDocPicker(true) : undefined}
          readOnly={!canEdit}
        />

        {/* Revision Notes */}
        {invoice.revision_notes && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Revision Notes</h3>
            <div className="text-purple-300 bg-purple-500/5 p-4 rounded-lg border border-purple-500/20">
              {invoice.revision_notes}
            </div>
          </div>
        )}

        {/* Revision History */}
        {invoice.revisions && invoice.revisions.length > 1 && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Revision History</h3>
            <div className="space-y-2">
              {invoice.revisions.map((rev: InvoiceRevision) => {
                const isCurrent = rev.id === invoice.id;
                return (
                  <div
                    key={rev.id}
                    className={`flex justify-between items-center p-3 rounded-lg border transition-colors ${
                      isCurrent
                        ? 'bg-primary/10 border-primary/30'
                        : 'bg-[#101922] border-gray-700 hover:border-gray-600 cursor-pointer'
                    }`}
                    onClick={() => {
                      if (!isCurrent && onViewRevision) {
                        onViewRevision(rev.id);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="text-white font-medium text-sm">
                          {rev.invoice_number}
                          {isCurrent && (
                            <span className="ml-2 text-xs text-primary">(current)</span>
                          )}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {formatDate(rev.created_at)}
                          {rev.revision_notes && ` — ${rev.revision_notes}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-white font-medium text-sm">
                        {formatCurrency(rev.total)}
                      </span>
                      <InvoiceStatusBadge status={rev.status} size="sm" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Hidden Printable Invoice for PDF/Print */}
        <div style={{ position: 'absolute', left: '-9999px', top: 0, backgroundColor: '#ffffff' }}>
          <PrintableInvoice ref={printableRef} invoice={invoice} companyInfo={companyInfo} invoiceFooter={invoiceFooter} />
        </div>
      </div>

      {/* Job Document Picker Modal */}
      {invoice.job_id && (
        <JobDocumentPickerModal
          isOpen={showDocPicker}
          onClose={() => setShowDocPicker(false)}
          jobId={String(invoice.job_id)}
          invoiceId={invoice.id}
          existingAttachments={invoice.attachments || []}
          onDocumentsLinked={handleAttachmentsChange}
        />
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="relative bg-gray-100 rounded-lg shadow-2xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowPreview(false)}
              className="absolute top-2 right-2 z-10 p-2 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition-colors shadow-lg"
              title="Close Preview"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
            {/* Action buttons */}
            <div className="sticky top-0 z-10 flex justify-center gap-2 p-3 bg-gray-800 rounded-t-lg">
              <button
                onClick={() => { handlePrint(); setShowPreview(false); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors"
              >
                <span className="material-symbols-outlined text-lg">print</span>
                Print
              </button>
              <button
                onClick={() => { handleDownloadPdf(); setShowPreview(false); }}
                disabled={generatingPdf}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-lg">download</span>
                {generatingPdf ? 'Generating...' : 'Download PDF'}
              </button>
            </div>
            {/* Invoice Preview */}
            <div className="p-4">
              <PrintableInvoice invoice={invoice} companyInfo={companyInfo} invoiceFooter={invoiceFooter} />
            </div>
          </div>
        </div>
      )}
      {showHoldbackConfirm && (
        <ConfirmDialog
          title="Release Holdback"
          message={`Release the held-back amount of ${formatCurrency(invoice.holdback_amount ?? 0)} back into the collectible balance? This cannot be undone.`}
          confirmLabel="Release Holdback"
          variant="default"
          onConfirm={handleReleaseHoldback}
          onCancel={() => setShowHoldbackConfirm(false)}
        />
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </Modal>
  );
};

// Inert "Pay Now" button — calls Stripe checkout, falls back gracefully
const PayNowButton = ({ invoiceId }: { invoiceId: number }) => {
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const handleClick = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.post(`/payments/create-link/${invoiceId}`);
      if (data.data?.url) {
        window.open(data.data.url, '_blank');
      }
    } catch (err: any) {
      if (err?.response?.status === 503) setUnavailable(true);
      else console.error('Pay Now error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (unavailable) return null;

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-60"
      title="Pay via Stripe"
    >
      <span className="material-symbols-outlined text-base">credit_card</span>
      {loading ? 'Opening…' : 'Pay Now'}
    </button>
  );
};
